(ns manage.content-configuration-page.context-source.dynamic-feed
  (:require ["@mui/material/Link$default" :as Link]
            ["@mui/material/Stack$default" :as Stack]
            [clojure.edn :refer [read-string]]
            [common.datascript.util :as du]
            [common.ui.list :as ui.list]
            [common.ui.store :as store]
            [common.widgets.layout :refer [stack-divider]]
            [datascript.core :as d]
            [manage.content-configuration-page.context-source.shared :as cs]
            [manage.content-configuration-page.view.input :as view.input]
            [manage.content-configuration-page.view.list :as lv]
            [re-frame.core :as rf]
            [re-posh.core :as rp]
            [reagent.ratom :as ratom]
            [reitit.frontend.easy :as r.easy]
            [uix.core :as uix :refer [$]]))

(def schema-id "urn:lemonpi:schema:context:content-variant:v1")

;; database

(rf/reg-sub :feed-context-source/options
  :<- [:resource.schema/parameter-schema schema-id]
  (fn [parameter-schema _]
    (get-in parameter-schema [:properties :feed-id :options])))

(rp/reg-event-ds :feed-context-source/set-feed-id
  (fn [_ [_ fcs feed-id]]
    (when feed-id
      [[:db.fn/retractAttribute fcs :feed-context-source/lookup]
       {:db/id fcs :feed-context-source/feed-id feed-id}])))

(rf/reg-sub-raw :feed-context-source/lookup-options
  (fn [_ [_ fcs]]
    (ratom/reaction
     (sort-by :label
              (for [[selector selector-schema] @(rf/subscribe [:context-source/selector-schemas @(store/entity fcs)])]
                {:value (name selector)
                 :label (:label selector-schema)})))))

(rf/reg-sub :feed-context-source/lookup-ids
  (fn [[_ fcs]]
    (store/entity fcs))
  (fn [{feed-lookups :feed-context-source/lookup} _]
    (map :feed-context-source-lookup/id feed-lookups)))

(defn- feed-context-source-lookup [fcs id]
  {:feed-context-source-lookup/owner-id fcs
   :feed-context-source-lookup/id id})

(rp/reg-event-ds :feed-context-source/sync-lookups
  (fn [ds [_ fcs to-be-assignments]]
    (du/sync-many
     fcs :feed-context-source/lookup
     (d/q '[:find ?l ?id :in $ ?fcs
            :where
            [?fcs :feed-context-source/lookup ?l]
            [?l :feed-context-source-lookup/id ?id]]
          ds fcs)
     to-be-assignments
     (partial feed-context-source-lookup fcs))))

(rp/reg-event-ds :feed-context-source-lookup/set-macro
  (fn [_ [_ l macro]]
    [[:db.fn/retractAttribute l :feed-context-source-lookup/macro]
     (assoc macro :feed-context-source-lookup/_macro l)]))

(rp/reg-pull-sub :feed-context-source-lookup/pull-macro
  '[{:feed-context-source-lookup/macro
     [:macro/selector
      {:context-source/_macro [*]}]}])

(rf/reg-sub :feed-context-source-lookup/get-macro
  (fn [[_ l]]
    (rf/subscribe [:feed-context-source-lookup/pull-macro l]))
  (fn [{macro :feed-context-source-lookup/macro} _]
    (when macro
      {:macro/selector (:macro/selector macro)
       :context-source/_macro
       [:context-source/alias (get-in macro [:context-source/_macro :context-source/alias])]})))

(defn- export-lookup [{:feed-context-source-lookup/keys [id macro] l :db/id}]
  {:pre [(some? l)]}
  ;; contains subscriptions inside a function that is also called by an event handler
  ;; re-frame does not like that, potential issue
  (let [context-source (get-in @(rf/subscribe [:feed-context-source-lookup/pull-macro l]) [:feed-context-source-lookup/macro :context-source/_macro])]
    {:from (when context-source
             {:urn (:context-source/schema context-source)
              :schema (:context-source/schema context-source)
              ;; Especially for feed context sources that refer to other feed
              ;; context sources, this structure can become very deeply nested.
              ;; Life would be much better if the content-variant service would
              ;; only require the alias+selector, and gather all required
              ;; information from the CFN itself
              :alias (:context-source/alias context-source)
              :param (cs/parameter context-source)
              :selector (first (:macro/selector macro))})
     :to id}))

(defmethod cs/parameter schema-id
  [{:feed-context-source/keys [feed-id lookup]}]
  (let [lookup-data (map export-lookup lookup)]
    {:feed-id feed-id
     :data {:in lookup-data}
     :dependencies (into #{} (comp (map :from) (map (juxt :urn :param))) lookup-data)}))

(defn- import-lookup [alias {:keys [from to]}]
  (cond-> (feed-context-source-lookup [:context-source/alias alias] to)
    (some? from)
    (assoc :feed-context-source-lookup/macro
           {:macro/selector [(keyword (:selector from))]
            ;; in the ideal situation we simply store a reference to the context
            ;; source using `[:context-source/alias (:alias from)]` but it could
            ;; be that the other context source is not imported yet (when doing
            ;; the initial import of a content function, causing a reference not
            ;; found error). So we always create a temporary context source,
            ;; which will be reused later (adding param), or if it already
            ;; exists, will be merged with the existing one (reusing param)
            :context-source/_macro {:context-source/schema (:urn from)
                                    :context-source/alias (:alias from)}})))

(defmethod cs/context->constructor schema-id
  [context]
  (when-let [feed-id (get-in context [:param :feed-id])]
    {:feed-context-source/feed-id feed-id
     :feed-context-source/lookup
     (map (partial import-lookup (:alias context)) (get-in context [:param :data :in]))}))

(def pull-pattern-feed-context-source-lookup
  [:feed-context-source-lookup/id
   {:feed-context-source-lookup/macro
    [:macro/selector
     {:context-source/_macro [:context-source/alias]}]}])

(def pull-pattern-feed-context-source
  ['*
   :feed-context-source/feed-id
   {:feed-context-source/lookup pull-pattern-feed-context-source-lookup}])

;; UI

(defn dropdown [e attr label options-sub props]
  [view.input/dropdown-save-on-change
   (-> (view.input/props-entity e attr)
       (merge props)
       (assoc :dropdown/options-sub options-sub)
       (assoc-in [:control-props :disabled?] @(rf/subscribe [:resource.content-function-version/active-version? "published"]))
       (assoc-in [:form-field-props :label] label))])

(defn lookup-macro [fcs l]
  [dropdown l :feed-context-source-lookup/macro
   "Is looked up by"
   [:context-source/all-macro-options-with-create-option #{@(store/get-value fcs :context-source/alias)}]
   {:dropdown/cast-value-fn #(when % (pr-str %))}])

(defn label-lookup-id [l]
  @(store/get-value l :feed-context-source-lookup/id))

(defn entity-view-cfg-lookup [fcs l]
  {:entity/ref l
   :entity/label [label-lookup-id l]
   :entity/icon "search"
   :validation/schema :feed-context-source-lookup
   :validation/pull-pattern pull-pattern-feed-context-source-lookup
   :list-item/details [lookup-macro fcs (assoc l
                                               :custom-setters
                                               {:feed-context-source-lookup/macro
                                                (fn [l macro-as-string]
                                                  (rf/dispatch [:feed-context-source-lookup/set-macro l (read-string macro-as-string)]))}
                                               :custom-getters
                                               {:feed-context-source-lookup/macro [:feed-context-source-lookup/get-macro]})]})

(defn choose-feed [fcs]
  [dropdown fcs :feed-context-source/feed-id
   "Select dynamic feed"
   [:feed-context-source/options]])

(defn add-lookup [fcs]
  [dropdown fcs :feed-context-source/lookup
   "Add lookup"
   [:feed-context-source/lookup-options fcs]
   {:control-props {:multi true :clearable true}
    :validation-value-fn (fn [selected-lookups]
                           (for [l selected-lookups]
                             {:feed-context-source-lookup/id l}))
    :validation/stages #{:warning}}])

(defmethod cs/entity-view-cfg schema-id
  [fcs]
  (let [link-context @(rf/subscribe [:router/path-params])]
    (merge
     (cs/entity-view-cfg-base fcs)
     {:validation/pull-pattern pull-pattern-feed-context-source
      :list-item/details [cs/configure-button fcs "rule" "Configure dynamic feed"]
      :entity/on-activate [[:manage.content-configuration-page.context-source.db/load-schema-output-structures]]
      :entity/read-only? false
      :entity/query-param [:context-source/query-params fcs]
      :entity/content
      [:> Stack {:divider ($ stack-divider)}
       [:> Stack {:p 3 :spacing 2}
        [choose-feed (assoc fcs :custom-setters {:feed-context-source/feed-id [:feed-context-source/set-feed-id]})]
        [add-lookup (assoc fcs :custom-getters {:feed-context-source/lookup [:feed-context-source/lookup-ids fcs]}
                           :custom-setters {:feed-context-source/lookup [:feed-context-source/sync-lookups]})]]
       [lv/view
        {:entity/ref fcs
         :list/items :feed-context-source/lookup
         :list/->item-cfg (partial entity-view-cfg-lookup fcs)
         :list/empty-msg ($ ui.list/empty-msg {:title "Configure dynamic feed"}
                            ($ :p
                               "Dynamic feeds allow you to leverage external
                                feed data in your dynamic configuration.")
                            ($ :p
                               "In order to use dynamic feeds you can select a dynamic feed
                                (which can be configured "
                               ($ Link {:href (r.easy/href :advertiser/feeds link-context)
                                        :target "_blank"} "here")
                               ").")
                            ($ :p
                               "To return a row of your dynamic feed you need to tell
                                the system which contextual data is used to lookup a
                                value in the defined column of your feed."))}]]})))

(defmethod cs/entity-query-params schema-id
  [{:keys [alias]}]
  (cs/alias->entity-query-params alias))
